import React, { useState, useEffect } from "react";
import {
  Layout, Button, Select, message, Card, Spin, Radio, Input, DatePicker, Row, Col
} from "antd";
import { useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { AuditOutlined, RightCircleOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import dayjs from "dayjs";

const { Content } = Layout;
const { Option } = Select;

const CreateGRN = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState("po"); // "po" or "direct"
  const [approvedPRs, setApprovedPRs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedPRId, setSelectedPRId] = useState(undefined);
  const [selectedSupplierId, setSelectedSupplierId] = useState(undefined);
  const [poSupplierId, setPoSupplierId] = useState(undefined);
  const [supplierRef, setSupplierRef] = useState("");
  const [supRefDate, setSupRefDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePoSupplierChange = (val) => {
    setPoSupplierId(val);
    setSelectedPRId(undefined);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Load active suppliers for GRN forms
        const resSups = await api.get("/masterdropdown/listof_activesuppliers.php");
        if (resSups.data && resSups.data.status === "success") {
          setSuppliers(resSups.data.dataset);
        }
      } catch (error) {
        message.error("Failed to load initial data.");
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!poSupplierId) {
      setApprovedPRs([]);
      return;
    }
    const fetchPrs = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/masterdropdown/listof_requested_prs_by_supplier.php?sup_id=${poSupplierId}`);
        if (res.data && res.data.status === "success") {
          setApprovedPRs(res.data.dataset);
        }
      } catch (error) {
        message.error("Failed to load PRs for selected supplier.");
      } finally {
        setLoading(false);
      }
    };
    fetchPrs();
  }, [poSupplierId]);

  const handleProceed = async () => {
    if (!supRefDate) {
      message.warning("Please select a Supplier Reference Date.");
      return;
    }
    if (!supplierRef || !supplierRef.trim()) {
      message.warning("Please enter Supplier Reference.");
      return;
    }
    if (mode === "po") {
      if (!selectedPRId) {
        message.warning("Please choose a Requested Purchase Request first.");
        return;
      }
      setLoading(true);
      try {
        const payload = {
          pr_id: selectedPRId,
          grn_status: "Draft",
          sup_ref: supplierRef,
          sup_ref_date: supRefDate.format("YYYY-MM-DD"),
          items: []
        };
        const res = await api.post("/inventory/grn/create_from_pr.php", payload);
        if (res.data && res.data.status === "success") {
          navigate(`/grn/receive/${selectedPRId}?grnId=${res.data.grn_id}`);
        } else {
          message.error(res.data.message || "Failed to initiate draft GRN.");
        }
      } catch (error) {
        message.error("Failed to initiate draft GRN.");
      } finally {
        setLoading(false);
      }
    } else {
      if (!selectedSupplierId) {
        message.warning("Please choose a Supplier first.");
        return;
      }
      setLoading(true);
      try {
        const payload = {
          pr_id: 0,
          sup_id: selectedSupplierId,
          grn_status: "Draft",
          sup_ref: supplierRef,
          sup_ref_date: supRefDate.format("YYYY-MM-DD"),
          items: []
        };
        const res = await api.post("/inventory/grn/create_from_pr.php", payload);
        if (res.data && res.data.status === "success") {
          navigate(`/grn/receive/0?supplierId=${selectedSupplierId}&grnId=${res.data.grn_id}`);
        } else {
          message.error(res.data.message || "Failed to initiate draft GRN.");
        }
      } catch (error) {
        message.error("Failed to initiate draft GRN.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
            <AppBreadcrumb items={[
              { label: 'Inventory', link: '/inventory-items' },
              { label: 'Create GRN' },
            ]} />
            <h1 className="fluid-page-title">
              <AuditOutlined /> Create Goods Received Note
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <p className="fluid-page-subtitle">Create a goods received note against a PR or direct from supplier</p>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content" style={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <Card className="fluid-card" style={{ width: "100%", maxWidth: "700px", padding: "10px" }}>
          <Spin spinning={loading}>
            <div style={{ marginBottom: 24, textAlign: "center" }}>
              <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                size="large"
                buttonStyle="solid"
                className="grn-mode-radio"
              >
                <Radio.Button value="po">Against Purchase Request (PR)</Radio.Button>
                <Radio.Button value="direct">Direct (Choose Supplier)</Radio.Button>
              </Radio.Group>
            </div>

            {mode === "po" ? (
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Select Supplier <span style={{ color: "#ff4d4f" }}>*</span>:</div>
                  <Select allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const label = option?.label || option?.children;
                      if (Array.isArray(label)) {
                        return label.join('').toLowerCase().includes(input.toLowerCase());
                      }
                      return String(label || '').toLowerCase().includes(input.toLowerCase());
                    }}
                    placeholder="Choose Supplier..."
                    style={{ width: "100%" }}
                    value={poSupplierId}
                    onChange={handlePoSupplierChange}
                    size="large"
                    className="fluid-select"
                  >
                    {suppliers.map((sup) => (
                      <Option key={sup.value} value={sup.value} label={sup.label}>
                        {sup.label}
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={12}>
                  <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Select Requested Purchase Request (PR) <span style={{ color: "#ff4d4f" }}>*</span>:</div>
                  <Select allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const label = option?.label || option?.children;
                      if (Array.isArray(label)) {
                        return label.join('').toLowerCase().includes(input.toLowerCase());
                      }
                      return String(label || '').toLowerCase().includes(input.toLowerCase());
                    }}
                    placeholder={poSupplierId ? "Choose PR Reference..." : "Please select a Supplier first"}
                    style={{ width: "100%" }}
                    value={selectedPRId}
                    onChange={setSelectedPRId}
                    size="large"
                    disabled={!poSupplierId}
                    className="fluid-select"
                  >
                    {approvedPRs.map((pr) => (
                      <Option key={pr.value} value={pr.value} label={`${pr.label} - ${pr.supplier_name}`}>
                        <span style={{ fontWeight: "600" }}>{pr.label}</span> — {pr.supplier_name} (LKR {Number(pr.pr_final_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })})
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            ) : (
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={24}>
                  <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Select Supplier <span style={{ color: "#ff4d4f" }}>*</span>:</div>
                  <Select allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const label = option?.label || option?.children;
                      if (Array.isArray(label)) {
                        return label.join('').toLowerCase().includes(input.toLowerCase());
                      }
                      return String(label || '').toLowerCase().includes(input.toLowerCase());
                    }}
                    placeholder="Choose Supplier..."
                    style={{ width: "100%" }}
                    value={selectedSupplierId}
                    onChange={setSelectedSupplierId}
                    size="large"
                    className="fluid-select"
                  >
                    {suppliers.map((sup) => (
                      <Option key={sup.value} value={sup.value} label={sup.label}>
                        {sup.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            )}

            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 20, marginTop: 20, marginBottom: 24 }}>
              <h3 style={{ margin: "0 0 16px 0", color: "var(--fluid-primary)", fontWeight: 600 }}>Receipt Configuration</h3>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier Reference Date <span style={{ color: "#ff4d4f" }}>*</span>:</div>
                  <DatePicker
                    placeholder="Select Date..."
                    style={{ width: "100%" }}
                    value={supRefDate}
                    onChange={setSupRefDate}
                    size="large"
                    className="fluid-input"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier Reference <span style={{ color: "#ff4d4f" }}>*</span>:</div>
                  <Input
                    placeholder="Enter Reference..."
                    value={supplierRef}
                    onChange={(e) => setSupplierRef(e.target.value)}
                    size="large"
                    className="fluid-input"
                  />
                </Col>
              </Row>
            </div>

            <Button
              type="primary"
              onClick={handleProceed}
              icon={<RightCircleOutlined />}
              disabled={mode === "po" ? !selectedPRId : !selectedSupplierId}
              className="fluid-btn-primary"
              block
            >
              Proceed to Receive Items
            </Button>
          </Spin>
        </Card>
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default CreateGRN;
